// The sidebar's remembered folds (notifications-overhaul slice 01, D11).
//
// One browser-local value, `dz-rail-folds` = `{ open: string[] }`: the ids of
// the rail sections that are open. It lists what is OPEN, not what is closed,
// so both defaults fall out of the shape instead of out of code:
//   - nothing stored (a first visit, or a value the schema refuses): every
//     section is folded;
//   - a section added later is in nobody's list, so it starts folded.
// The store does not know which ids exist. An id no section carries is never
// asked about, so it changes nothing.
//
// GLOBAL, not org-scoped, like `dz-terminal-layout`: a fold is a preference
// about the chrome, not account data. Written on the mutation, not from an
// effect, so the store works outside a reactive root.

import { createSignal } from 'solid-js';
import { z } from 'zod';
import { readLocalPref, writeLocalPref } from './local-pref';

export const RAIL_FOLDS_KEY = 'dz-rail-folds';

const RailFoldsSchema = z.object({ open: z.array(z.string()) });

export type RailFolds = {
  /** Reactive: read it in JSX or an effect and it re-runs when a fold changes. */
  isOpen(id: string): boolean;
  /** The heading's click: open if folded, fold if open. */
  toggle(id: string): void;
  /** Arriving in a section: open it and remember that. Never folds. */
  reveal(id: string): void;
};

export function createRailFolds(): RailFolds {
  const [open, setOpen] = createSignal<readonly string[]>(
    readLocalPref(RAIL_FOLDS_KEY, RailFoldsSchema, { open: [] }).open,
  );
  const commit = (next: readonly string[]): void => {
    setOpen(next);
    writeLocalPref(RAIL_FOLDS_KEY, { open: next });
  };
  return {
    isOpen: (id) => open().includes(id),
    toggle: (id) =>
      commit(open().includes(id) ? open().filter((other) => other !== id) : [...open(), id]),
    reveal: (id) => {
      if (!open().includes(id)) commit([...open(), id]);
    },
  };
}
